import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { Slide, ToastContainer } from 'react-toastify';

import AppSwitch from './AppSwitch';
import { ENTRA_POPUP_RELAY_PATH, ENTRA_REDIRECT_PATH } from './auth/entraAuthConfig';
import { isPopupRelaySupported, readPopupRelayRequest, runEntraPopupRelay } from './auth/entraPopupRelay';
import AudioNotification from './components/AudioNotification';
import { CallContextProvider } from './contexts/CallContext';
import { PlannedCallContextProvider } from './contexts/PlannedCallContext';
import ApiActivityMonitor from './utils/ApiActivityMonitor';
import queryClientConfig from './utils/queryClientConfig';

const logAuthPageError = (message: string) => (error: unknown) => {
  // eslint-disable-next-line no-console
  console.error(message, error);
};

if (window.location.pathname === ENTRA_REDIRECT_PATH) {
  import('@azure/msal-browser/redirect-bridge')
    .then(({ broadcastResponseToMainFrame }) => broadcastResponseToMainFrame())
    .catch(logAuthPageError('Entra redirect bridge failed'));
} else if (window.location.pathname === ENTRA_POPUP_RELAY_PATH) {
  const relayRequest = readPopupRelayRequest();
  const container = document.getElementById('root');

  if (container) {
    container.setAttribute(
      'style',
      'display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;height:100vh;font-family:sans-serif;text-align:center'
    );

    const message = document.createElement('p');
    message.textContent = 'Continue to sign in to AMP with your Matic account.';

    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = 'Continue';
    button.setAttribute(
      'style',
      'padding:8px 24px;font:inherit;font-weight:600;color:#fff;background:#3057e1;border:0;border-radius:4px;cursor:pointer'
    );
    button.addEventListener(
      'click',
      () => {
        if (isPopupRelaySupported(relayRequest)) {
          runEntraPopupRelay(relayRequest);
          return;
        }

        import('@azure/msal-browser/popup-relay')
          .then(({ runPopupRelay }) => runPopupRelay())
          .catch(logAuthPageError('Entra popup relay failed'));
      },
      { once: true }
    );

    container.append(message, button);
    button.focus();
  }
} else {
  window.__amp_config = {};
  window.__amp_feature_flags = {};
  if (
    // clear storage if it exceeds 2mb
    Object.keys(window.localStorage)
      .map(k => ((window.localStorage.getItem(k) ?? '').length * 2) / 1024 / 1024)
      .reduce((a, b) => a + b, 0) > 2
  ) {
    window.localStorage.clear();
  }

  const container = document.getElementById('root') as Element;
  const root = createRoot(container);

  const queryClient = new QueryClient(queryClientConfig);

  root.render(
    <QueryClientProvider client={queryClient}>
      <BrowserRouter useTransitions={false}>
        <CallContextProvider>
          <PlannedCallContextProvider>
            <AppSwitch />
            <ToastContainer transition={Slide} limit={1} />
            <AudioNotification />
          </PlannedCallContextProvider>
        </CallContextProvider>
      </BrowserRouter>
      <ApiActivityMonitor />
      {import.meta.env.DEV && <ReactQueryDevtools />}
    </QueryClientProvider>
  );
}
